import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CarFront, ExternalLink } from 'lucide-react';

interface AuthLayoutProps {
  children: ReactNode;
}

export const AuthLayout = ({ children }: AuthLayoutProps) => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-gray-200">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="hidden lg:block relative bg-gradient-to-br from-primary-700 via-primary-600 to-primary-900 text-white p-10">
            <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-white/10 blur-2xl" />

            <div className="relative h-full flex flex-col">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-white/15 ring-1 ring-white/20 flex items-center justify-center">
                  <CarFront className="h-6 w-6" />
                </div>
                <div className="text-2xl font-semibold tracking-tight">
                  {t('common.appName')}
                </div>
              </div>

              <div className="mt-10">
                <div className="text-3xl font-semibold leading-tight">
                  {t('auth.brand.title', 'Track your vehicle with confidence')}
                </div>
                <div className="mt-4 text-white/85 leading-relaxed">
                  {t(
                    'auth.brand.description',
                    'Maintenance, expenses, reminders and analytics — all in one place.'
                  )}
                </div>
              </div>

              <div className="mt-auto pt-10 text-xs text-white/70">
                <a
                  href="/about"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-xs font-medium text-white ring-1 ring-white/20 hover:bg-white/15"
                >
                  <ExternalLink className="h-4 w-4" />
                  {t('auth.brand.aboutLink', 'About project')}
                </a>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-10">{children}</div>
        </div>
      </div>
    </div>
  );
};
